/* eslint-disable @typescript-eslint/no-unused-vars */
import { startRefreshAccessTokenAtRegularInterval } from '@auth/api/auth';
import Spinner from '@components/spinner';
import themeV3 from '@config/theme';
import ErrorPage from '@containers/error';
import SelectEvaluationPartner from '@containers/evaluation-partner';
import FormVerification from '@containers/form-verification';
import Information from '@containers/information';
import { configs } from '@containers/information/configs';
import Payment from '@containers/payment';
import TnC from '@containers/tnc';
import PrivacyPolicy from '@containers/tnc/privacy-policy';
import { FORM_URL_KEY } from '@global/constants/form';
import { DEFAULT_ORG } from '@global/constants/general';
import { isClientCareerMosaic, safeLocalStorageGetItem } from '@global/helpers/general';
import { SentryRoutes } from '@global/monitoring/sentry';
import { Box, ThemeProvider } from '@mui/material';
import { RootState } from '@redux/store';
import { setNavigateFunction } from '@services/navigation';
import { setEnqueueFunction } from '@services/snackbar';
import { useSnackbar, VariantType } from 'notistack';
import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Route, useLocation, useNavigate } from 'react-router-dom';
import DevEvalLink from './dev/eval-link';
import GlobalStyles from './global-styles';
import Wallet from './wallet';

const App = () => {
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const navigate = useNavigate();
  const isMounted = useRef(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const auth: any = useSelector((s: RootState) => s.auth);
  const { accessToken, version } = auth;
  const currentVersion = useRef<string | null>(null);

  if (!isMounted.current) {
    setNavigateFunction(navigate);
    setEnqueueFunction((msg: string, variant: VariantType) => {
      enqueueSnackbar({
        variant,
        message: msg,
        preventDuplicate: true,
        className: 'snackbar',
        style: { overflowWrap: 'anywhere', padding: '1em' }
      });
      // if (msg) {
      //   let key;
      //   // eslint-disable-next-line @typescript-eslint/no-unused-vars, prefer-const
      //   key = enqueueSnackbar(<CustomSnackbar msg={msg} snackbarKey={key} />, {
      //     preventDuplicate: true,
      //     variant,
      //     style: { overflowWrap: 'anywhere', zIndex: '9999999 !important' },
      //     className: 'snackbar',
      //     anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
      //     autoHideDuration: 7000
      //   });
      // }
    });
    const sso = !isClientCareerMosaic() ? query.get('sso') : undefined;
    if ((safeLocalStorageGetItem('refreshToken') || sso) && !window.intervalId) {
      startRefreshAccessTokenAtRegularInterval(sso || undefined);
    }
  }

  const renderAuthPage = () => {
    let Component = null;
    if (import.meta.env.VITE_NSDC_PARTNER) {
      Component = React.lazy(() => import('@auth/custom/dynamic-nsdc-ui'));
    } else if (import.meta.env.VITE_ORG !== DEFAULT_ORG) {
      Component = React.lazy(() => import('@auth/custom/dynamic/index-new'));
    } else {
      Component = React.lazy(() => import('@auth/index'));
    }
    return Component;
  };

  useEffect(() => {
    isMounted.current = true;
  }, []);

  useEffect(() => {
    if (import.meta.env.DEV) return;

    const checkVersion = async () => {
      try {
        const res = await fetch(`/meta.json?_=${Date.now()}`);
        const data = await res.json();

        // Store first version
        if (currentVersion.current === null) {
          currentVersion.current = data.version;
          return;
        }

        // Version changed
        if (currentVersion.current !== data.version) {
          const now = Date.now();
          const url = new URL(window.location.href);

          url.searchParams.set('time', now.toString());

          window.location.href = url.toString();

          // Update reference to avoid repeated prompts
          currentVersion.current = data.version;
        }
      } catch (err) {
        console.error('Version check failed:', err);
      }
    };

    checkVersion();

    // Poll every 10 seconds
    const interval = setInterval(checkVersion, 60000);
    return () => clearInterval(interval);
  }, []);

  const AuthComponentToRender = renderAuthPage();

  const commonRoutes = (
    <>
      <Route path={`/${FORM_URL_KEY}/:formId/:txnId?`} element={<FormVerification />} />
      <Route path="/payment" element={<Payment />} />
      {import.meta.env.VITE_ENV === 'development' && (
        <Route path="/dev/eval-link" element={<DevEvalLink />} />
      )}
      {isClientCareerMosaic() && (
        <>
          {' '}
          <Route path="/select-evaluation-partner" element={<SelectEvaluationPartner />} />
        </>
      )}
      {configs.faq ? (
        <>
          {!import.meta.env.VITE_NSDC_PARTNER ? (
            <Route path={`/faq`} element={<Information />} />
          ) : null}
          <Route path={`/instructions`} element={<Information />} />
        </>
      ) : null}
      <Route path="/error" element={<ErrorPage />} />
      <Route path={`/terms`} element={<TnC />} />
      <Route path={`/privacy-policy`} element={<PrivacyPolicy />} />
    </>
  );
  if (accessToken) {
    return (
      <ThemeProvider theme={themeV3}>
        <GlobalStyles />
        <Wallet commonRoutes={commonRoutes} />
      </ThemeProvider>
    );
  } else {
    return (
      <ThemeProvider theme={themeV3}>
        <GlobalStyles />
        <Box
          sx={{
            height: '100vh',
            display: 'flex',
            flexFlow: 'column'
            // ml: isDesktop() ? '30%' : 'auto',
            // mr: isDesktop() ? '30%' : 'auto'
          }}>
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              // height: '100vh'
              overflowY: accessToken ? 'hidden' : 'auto',
              backgroundColor: '#F6F9FC'
            }}>
            <React.Suspense fallback={<Spinner />}>
              <SentryRoutes>
                {accessToken ? null : (
                  <>
                    {commonRoutes}
                    {/* <Route path="/login" element={<Login />} /> */}
                    {isClientCareerMosaic() ? null : (
                      <Route
                        path="/login"
                        element={
                          <div data-marker="login-page">
                            <AuthComponentToRender />
                          </div>
                        }
                      />
                    )}

                    <Route
                      path="*"
                      element={
                        isClientCareerMosaic() ? (
                          <Navigate
                            to="/error"
                            state={{
                              message: 'Session timed out, Please generate new link',
                              hideBackButton: true
                            }}
                          />
                        ) : (
                          <Navigate to="/login" />
                        )
                      }
                    />
                  </>
                )}
              </SentryRoutes>
            </React.Suspense>
          </div>
        </Box>
      </ThemeProvider>
    );
  }
};

export default App;
